/**
 * Interaction Metrics Service
 * Matches engagement metrics pattern: send metrics directly, not batched
 */

/**
 * POST interaction metric to the API (send individual metrics, not batched)
 */
export const postInteractionMetric = async (
  metricData,
  apiUrl = process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai/api/v1'
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(
      `${apiUrl}/analytics/interaction`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          sessionId: metricData.sessionId,
          experienceId: metricData.experienceId,
          page: metricData.page,
          buttonId: metricData.buttonId,
          scrollDepth: metricData.scrollDepth,
          interactionType: metricData.interactionType,
          deviceType: metricData.deviceType,
          deviceId: metricData.deviceId,
          timestamp: metricData.timestamp
        }),
        credentials: 'include',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`Failed to post interaction metric: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error posting interaction metric:', error);
    throw error;
  }
};

/**
 * Fetch interaction metrics from the API
 */
export const fetchInteractionMetrics = async (
  filters = {},
  apiUrl = process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai/api/v1'
) => {
  try {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found. Please login again.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const queryParams = new URLSearchParams();
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.interactionType) queryParams.append('interactionType', filters.interactionType);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.experienceId) queryParams.append('experienceId', filters.experienceId);
    if (filters.buttonId) queryParams.append('buttonId', filters.buttonId);
    if (filters.deviceType) queryParams.append('deviceType', filters.deviceType);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.offset) queryParams.append('offset', filters.offset);

    const queryString = queryParams.toString();
    const url = queryString 
      ? `${apiUrl}/analytics/interaction?${queryString}`
      : `${apiUrl}/analytics/interaction`;

    const response = await fetch(
      url,
      {
        method: 'GET',
        headers,
        credentials: 'include',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`Failed to fetch interaction metrics: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching interaction metrics:', error);
    throw error;
  }
};

export default {
  postInteractionMetric,
  fetchInteractionMetrics,
};
